import React, { FC } from 'react';
import {
  Badge,
  Button,
  ButtonIcon,
  Gray100,
  Gray20,
  Orange80,
  Red80,
  typeCompact12Medium,
  typeCompact14Medium,
  White,
} from '@traceair/tds';
import { classes, media, style, stylesheet } from 'typestyle';
import { DocType } from '../../../../../types';
import NewDocPlaceholder from './NewDocPlaceholder';
import InfoHint from './InfoHint';
import { format } from 'date-fns';
import DocPlaceholder from './DocPlaceholder';
import { trueDaysDiff } from '../../../../../libs/trueDateDiff';
import { DownloadIcon } from '@traceair/webapp-icons';

type DocumentCardLayoutProps = {
  docType: DocType;
  projectName?: string;
  isFixNeeded?: boolean;
  warningDate?: Date;
  startDate?: Date;
  endDate?: Date;
  thumbnail?: {
    filename: string;
    url?: string;
  };
  isNewDocument?: boolean;
  button?: {
    title: string;
    onClick: () => void;
  };
  onCardClick?: () => void;
  onDownloadAllClick?: () => void; // will show download all button
};

const DocumentCardLayout: FC<DocumentCardLayoutProps> = ({
  docType,
  projectName,
  isFixNeeded,
  warningDate,
  startDate,
  endDate,
  thumbnail,
  isNewDocument,
  button,
  onCardClick,
  onDownloadAllClick,
}) => {
  function handleButtonClick(e?: React.MouseEvent<Element, MouseEvent>) {
    e?.stopPropagation();
    button?.onClick();
  }

  function handleDownloadAllClick(e?: React.MouseEvent<Element, MouseEvent>) {
    e?.stopPropagation();

    if (onDownloadAllClick) {
      onDownloadAllClick();
    }
  }

  return (
    <div
      className={classes(rootStyle, onCardClick && css.activeCard, onDownloadAllClick && css.rootWithDownloadAll)}
      onClick={onCardClick ? onCardClick : undefined}
    >
      {/* Note the reverse order of blocks inside the root. The trick lets Info container occupy necessary space at the bottom */}
      <div className={css.infoContainer}>
        {onDownloadAllClick && (
          <>
            <ButtonIcon
              icon={<DownloadIcon />}
              type="black"
              style="secondary"
              className={classes('download-all-button', css.downloadAll)}
              onClick={handleDownloadAllClick}
            />
          </>
        )}
        <div className={css.titleContainer}>
          <div>{docType}</div>
          <div className={css.hint}>
            <InfoHint docType={docType} />
          </div>
        </div>
        {projectName && (
          <div
            className={typeCompact14Medium}
            style={{ marginTop: isFixNeeded || warningDate ? 4 : 12, overflowWrap: 'break-word' }}
          >
            {projectName}
          </div>
        )}
        {isFixNeeded && (
          <div className={typeCompact12Medium}>
            <Badge className={css.badge} type="negative" label="Need to fix" />
            <div style={{ color: Red80 }}>Please update document</div>
          </div>
        )}
        {warningDate && !isFixNeeded && <ExpirationWarning date={warningDate} />}
        {isNewDocument && (
          <div className={classes(typeCompact14Medium, css.datesAndPrefFormats)}>Preferred formats: PDF, DOC, JPG</div>
        )}
        {!isNewDocument && (startDate || endDate) && (
          <div className={classes(typeCompact14Medium, css.datesAndPrefFormats)}>
            {getDateFormatted(startDate, endDate)}
          </div>
        )}
        {button && (
          <Button
            onClick={handleButtonClick}
            text={button.title}
            style="secondary"
            className={style({ marginTop: 12 })}
            fullWidth
          />
        )}
      </div>
      <Picture isNewDocument={isNewDocument} thumbnail={thumbnail} />
    </div>
  );
};

const Picture: FC<{
  isNewDocument?: boolean;
  thumbnail?: {
    filename: string;
    url?: string;
  };
}> = ({ isNewDocument, thumbnail }) => {
  return (
    // combination of two wrappers pictureRoot and pictureWrap helps to achieve needed vertical alignment:
    // center if fit, otherwise align top and hide overflow
    <div className={css.pictureRoot}>
      <div className={css.pictureWrap}>
        {thumbnail &&
          (thumbnail.url ? (
            <img className={css.picture} src={thumbnail.url} alt="thumbnail" />
          ) : (
            <div className={css.thumbnailContainer}>
              <DocPlaceholder />
              <div className={classes(typeCompact14Medium, css.fileName)}>
                {getFileNameStr(decodeURIComponent(thumbnail.filename))}
              </div>
            </div>
          ))}
        {(isNewDocument || !thumbnail) && <NewDocPlaceholder />}
      </div>
    </div>
  );
};

const ExpirationWarning: FC<{ date: Date }> = ({ date }) => {
  const { daysDiff, daysDiffAbsStr } = trueDaysDiff(date);

  const isExpired = daysDiff < 0;
  const labelMessage =
    daysDiff < 0
      ? `Overdue ${daysDiffAbsStr} ago`
      : daysDiff === 0
        ? 'Expiring today'
        : `Expiring in ${daysDiffAbsStr}`;

  return (
    <div className={typeCompact12Medium}>
      <Badge
        className={classes(css.badge, !isExpired && css.badgeWarning)}
        label={labelMessage}
        type={isExpired ? 'negative' : 'warning'}
      />
      <div style={{ color: isExpired ? Red80 : Orange80 }}>
        {isExpired ? 'Please upload new version asap' : 'Please upload new version till expiring date'}
      </div>
    </div>
  );
};

function getDateFormatted(startDate?: Date, endDate?: Date) {
  if (!startDate && !endDate) {
    return 'Indefinite';
  }

  if (!startDate && endDate) {
    return `until ${format(endDate, 'd MMM, yyy')}`;
  }

  if (startDate && !endDate) {
    return `from ${format(startDate, 'd MMM, yyy')}`;
  }

  return `${format(startDate!, 'd MMM, yyy')} – ${format(endDate!, 'd MMM, yyy')}`;
}

function getFileNameStr(fileName: string) {
  if (!fileName) return '';

  const nameRX = /.*\./;
  const name = nameRX.exec(fileName)?.[0] ?? '';
  const extension = fileName.split('.').pop();

  return (
    <>
      <div style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>{name}</div>
      <div>{extension}</div>
    </>
  );
}

const rootStyle = style(
  {
    width: 260,
    height: 420,
    backgroundColor: White,
    borderRadius: 16,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column-reverse',
    border: `1px solid ${Gray20}`,
    $nest: {
      '&:hover': {
        boxShadow: '0px 6px 20px 0px rgba(0, 0, 0, 0.20), 0px 3px 20px 0px rgba(0, 0, 0, 0.10)',
        zIndex: 1, // for shadow to be on top of the next (on the right) element
      },
    },
  },
  media({ minWidth: 1088 }, { width: 300 }), // wide card
);

const css = stylesheet({
  rootWithDownloadAll: {
    $nest: {
      '&:hover .download-all-button': {
        display: 'flex',
      },
    },
  },
  infoContainer: {
    position: 'relative',
    padding: 16,
    borderTop: `solid 1px ${Gray20}`,
  },
  activeCard: {
    cursor: 'pointer',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: -0.25,
    lineHeight: '20px',
    flexShrink: 0,
  },
  hint: {
    paddingTop: 2,
  },
  pictureRoot: {
    flex: '1 1',
    overflow: 'hidden',
    padding: '24px 24px 0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pictureWrap: {
    maxHeight: '100%',
    overflow: 'hidden',
  },
  datesAndPrefFormats: {
    color: Gray100,
    marginTop: 12,
  },
  downloadAll: {
    display: 'none',
    position: 'absolute',
    top: -44,
    right: 8,
    cursor: 'pointer',
  },
  badge: {
    margin: '12px 0 4px 0',
  },
  badgeWarning: {
    background: Orange80 + '!important',
    color: White + ' !important',
  },
  thumbnailContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  fileName: {
    width: '100%',
    color: Gray100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  picture: {
    width: '100%',
    borderRadius: 12,
    border: `1px solid ${Gray20}`,
    boxSizing: 'border-box',
  },
});

export default DocumentCardLayout;
